import { ActionFunction, replace } from 'react-router-dom';
import { client } from '~/api/client';
import {
  parseHeaderFooterFromFormData,
  parseStyleFromFormData,
  parseVisibilityFormField
} from '~/api/data/form-data';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CreateHtmlDocumentTemplateDocument,
  CreateHtmlDocumentTemplateMutation,
  CreateHtmlDocumentTemplateMutationVariables,
  UpdateHtmlDocumentTemplateDocument,
  UpdateHtmlDocumentTemplateMutation,
  UpdateHtmlDocumentTemplateMutationVariables,
  UpdateHtmlDocumentTemplatesDocument,
  UpdateHtmlDocumentTemplatesMutation,
  UpdateHtmlDocumentTemplatesMutationVariables,
  type DocumentPagesSource,
  type HtmlDocumentTemplateInput,
  type VisibilityConfigInput
} from '~/api/graphql';

// Styles and templates share mutations, but redirects must keep the originating
// library tab so the user sees the matching list and edit dialog.
function htmlDocumentTemplateListBase(
  request: Request
): '/app/library/templates' | '/app/library/styles' {
  const path = new URL(request.url).pathname;
  return path.startsWith('/app/library/templates')
    ? '/app/library/templates'
    : '/app/library/styles';
}

// Present only when designs are on in the editor. Leaving a key out keeps the saved value, so a
// submit without them (styles, designs off) cannot clear a template's design or page choices.
const parseDesignFields = (
  formData: FormData
): Pick<
  HtmlDocumentTemplateInput,
  'designId' | 'startPages' | 'endPages' | 'printsOwnHeaderFooter'
> => ({
  ...(formData.has('designId') && { designId: (formData.get('designId') as string) || null }),
  ...(formData.has('startPages') && {
    startPages: formData.get('startPages') as DocumentPagesSource
  }),
  ...(formData.has('endPages') && { endPages: formData.get('endPages') as DocumentPagesSource }),
  ...(formData.has('printsOwnHeaderFooter') && {
    printsOwnHeaderFooter: formData.get('printsOwnHeaderFooter') === 'true'
  })
});

export const createHtmlDocumentTemplate = (
  variables: CreateHtmlDocumentTemplateMutationVariables
) =>
  mutateData<CreateHtmlDocumentTemplateMutation, CreateHtmlDocumentTemplateMutationVariables>(
    client,
    {
      mutation: CreateHtmlDocumentTemplateDocument,
      variables
    }
  );

export const updateHtmlDocumentTemplate = (
  variables: UpdateHtmlDocumentTemplateMutationVariables
) =>
  mutateData<UpdateHtmlDocumentTemplateMutation, UpdateHtmlDocumentTemplateMutationVariables>(
    client,
    {
      mutation: UpdateHtmlDocumentTemplateDocument,
      variables
    }
  );

export const archiveHtmlDocumentTemplates = (
  variables: UpdateHtmlDocumentTemplatesMutationVariables
) =>
  mutateData<UpdateHtmlDocumentTemplatesMutation, UpdateHtmlDocumentTemplatesMutationVariables>(
    client,
    {
      mutation: UpdateHtmlDocumentTemplatesDocument,
      variables
    }
  );

export const updateHtmlDocumentTemplatesVisibility = async ({
  templateIds,
  visibility
}: {
  templateIds: string[];
  visibility: VisibilityConfigInput;
}) => {
  try {
    await client.mutate<
      UpdateHtmlDocumentTemplatesMutation,
      UpdateHtmlDocumentTemplatesMutationVariables
    >({
      mutation: UpdateHtmlDocumentTemplatesDocument,
      variables: { templateIds, input: { visibility } }
    });
    return { failedIds: [] as string[] };
  } catch {
    return { failedIds: templateIds };
  }
};

export const submitArchiveHtmlDocumentTemplates: ActionFunction = async ({ request }) =>
  submitMutation(
    archiveHtmlDocumentTemplates({
      templateIds: (await request.json()).templateIds,
      input: { archive: true }
    })
  );

export const submitUpdateHtmlDocumentTemplatesVisibility: ActionFunction = async ({ request }) =>
  updateHtmlDocumentTemplatesVisibility(await request.json());

export const submitCreateHtmlDocumentTemplate: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const style = parseStyleFromFormData(formData);
  const listBase = htmlDocumentTemplateListBase(request);
  return submitMutation(
    createHtmlDocumentTemplate({
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        baseStyleId: (formData.get('baseStyleId') as string | null) || null,
        prefixFileId: (formData.get('prefixFileId') as string | null) || null,
        suffixFileId: (formData.get('suffixFileId') as string | null) || null,
        uploadPrefixFile: (formData.get('uploadPrefixFile') as File | null) || null,
        uploadSuffixFile: (formData.get('uploadSuffixFile') as File | null) || null,
        bodyTemplate: (formData.get('bodyTemplate') as string | null) || null,
        headerFooter: parseHeaderFooterFromFormData(formData),
        visibility: parseVisibilityFormField(formData.get('visibility')),
        ...parseDesignFields(formData),
        ...(style !== undefined && { style })
      }
    }),
    async (data) => replace(`${listBase}/${data?.createHtmlDocumentTemplate?.id}`)
  );
};

export const submitUpdateHtmlDocumentTemplate: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  const style = parseStyleFromFormData(formData);
  const listBase = htmlDocumentTemplateListBase(request);
  return submitMutation(
    updateHtmlDocumentTemplate({
      id: params.id || '',
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        baseStyleId: (formData.get('baseStyleId') as string | null) || null,
        prefixFileId: (formData.get('prefixFileId') as string | null) || null,
        suffixFileId: (formData.get('suffixFileId') as string | null) || null,
        uploadPrefixFile: (formData.get('uploadPrefixFile') as File | null) || null,
        uploadSuffixFile: (formData.get('uploadSuffixFile') as File | null) || null,
        bodyTemplate: (formData.get('bodyTemplate') as string | null) || null,
        headerFooter: parseHeaderFooterFromFormData(formData),
        visibility: parseVisibilityFormField(formData.get('visibility')),
        ...parseDesignFields(formData),
        ...(style !== undefined && { style })
      }
    }),
    async (data) => replace(`${listBase}/${data?.updateHtmlDocumentTemplate?.id}`)
  );
};
